// Admin workspace > "Data access" tab (admin.ts; platform_admin only) — P1 of
// reports/multi_org_data_architecture_2026-09-30.md §2.3. Per client: which
// restricted / licensed datasets it is granted (with the evidence, and the
// data OWNER labelled next to every control — user decision 3), its council
// scope, its compute profile, the per-dataset counts of what enforcement
// hides (or, in shadow mode, would hide) and, since P7a, the requests the
// enforcing middleware denied without a dataset key (outside the GIS view,
// unclassified path).
// Backend: src/wcc_viewer/routers/data_access.py, registry in
// config/data_registry.yaml.
import { getJson, postJson } from "./api";
import { el } from "./legend";
import { showToast } from "./toast";

interface DsGrant {
  id: number;
  dataset_key: string;
  granted_by_username: string | null;
  granted_at: string;
  evidence: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_by_username: string | null;
  revoke_reason: string | null;
  status: "active" | "revoked" | "expired";
}

interface DsRow {
  key: string;
  title: string;
  class: string;
  class_label: string;
  owner: string;
  owner_client_slug: string | null;
  licensee: string | null;
  licensed_use_client_slugs: string[];
  licence: string | null;
  provisional: boolean;
  attribution: string | null;
  expires: string | null;
  expired: boolean;
  decision_pending: string | null;
  notes: string | null;
  always_on: boolean;
  grantable: boolean;
  grant: DsGrant | null;
  entitled: boolean;
  grant_block_reason: string | null;
  shadow_hits_7d: number;
  columns: string[];
  reference_layers: string[];
}

interface ShadowCount {
  client_id: number;
  client_name: string | null;
  dataset_key: string;
  surface: string;
  hits: number;
  last_at: string;
  last_path: string | null;
  last_username: string | null;
}

interface ClientDetail {
  client: { id: number; name: string; slug: string };
  mode: string;
  councils: string[];
  all_councils: string[];
  state: { target_profile_id: string | null; serving: string };
  context: { profile_id: string; public_profile_id: string };
  datasets: DsRow[];
  history: DsGrant[];
  shadow: ShadowCount[];
  class_labels: Record<string, string>;
}

interface MatrixClient {
  id: number;
  name: string;
  slug: string;
  councils: string[];
  granted: string[];
  profile_id: string;
  shadow_hits_7d: number;
}

interface Matrix {
  mode: string;
  public_profile_id: string;
  clients: MatrixClient[];
  grantable_datasets: { key: string; title: string; owner: string; class: string; granted_to: number[] }[];
}

interface ShadowLog {
  counts: ShadowCount[];
  recent: { at: string; client_name: string | null; username: string | null; path: string; missing: string[] }[];
  unclassified: { path: string; requests: number }[];
  requests_checked: number;
  requests_would_hide: number;
  // P7a: refusals with no dataset key (this process's recent ring)
  denied?: { at: string; client_name: string | null; username: string | null; path: string; reason: string }[];
  requests_denied?: number;
}

const CLASS_ORDER = ["restricted_owner", "restricted_derived", "licensed", "prohibited", "public", "internal", "client_private"];
const COLLAPSED_CLASSES = new Set(["public", "internal", "client_private"]);

let hostEl: HTMLElement | null = null;
let matrixCache: Matrix | null = null;
let detailCache: ClientDetail | null = null;
let shadowCache: ShadowLog | null = null;
let selectedClient: number | null = null;
let openForm: { key: string; kind: "grant" | "revoke" } | null = null;
let errorText = "";

function redraw(): void {
  if (hostEl) draw(hostEl);
}

async function loadAll(): Promise<void> {
  try {
    matrixCache = await getJson<Matrix>("/api/data-access/clients");
    if (selectedClient === null && matrixCache.clients.length > 0) selectedClient = matrixCache.clients[0].id;
    if (selectedClient !== null) detailCache = await getJson<ClientDetail>(`/api/data-access/clients/${selectedClient}`);
    shadowCache = await getJson<ShadowLog>("/api/data-access/shadow?days=7");
    errorText = "";
  } catch (e) {
    errorText = e instanceof Error ? e.message : String(e);
  }
  redraw();
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "";
}

/** Dataset title + key + its data OWNER, labelled right next to the grant
 * control (user decision 3). One cell so the table fits the sidebar. */
function datasetCell(d: DsRow): HTMLElement {
  const td = el("td", {});
  td.append(el("div", {}, d.title));
  const owner = el("div", {}, `Data owner: ${d.owner}`);
  owner.style.fontWeight = "600";
  td.append(owner);
  if (d.class === "licensed" && d.licensee) td.append(el("div", { className: "caption" }, `Licensee: ${d.licensee}`));
  td.append(el("div", { className: "caption" }, d.key));
  return td;
}

/** Tables scroll sideways inside the sidebar rather than squashing. */
function scrollWrap(table: HTMLElement): HTMLElement {
  const wrap = el("div");
  wrap.style.overflowX = "auto";
  wrap.append(table);
  return wrap;
}

function evidenceCell(d: DsRow): HTMLElement {
  const td = el("td", { className: "caption" });
  if (d.grant) {
    td.append(el("div", {}, `"${d.grant.evidence}"`));
    td.append(el("div", {}, `granted ${fmtDate(d.grant.granted_at)} by ${d.grant.granted_by_username ?? "P1 seed"}`));
    if (d.grant.expires_at) td.append(el("div", {}, `expires ${d.grant.expires_at}`));
  } else if (d.licence) {
    td.append(el("div", {}, d.licence));
  }
  if (d.provisional) {
    const w = el("div", {}, "Licence not yet confirmed (public-provisional)");
    w.style.color = "var(--status-warning)";
    td.append(w);
  }
  if (d.expires) {
    const w = el("div", {}, `${d.expired ? "EXPIRED" : "Registry expiry"} ${d.expires}`);
    w.style.color = d.expired ? "var(--status-critical)" : "var(--status-warning)";
    td.append(w);
  }
  if (d.decision_pending) {
    const w = el("div", {}, `Open decision: ${d.decision_pending}`);
    w.style.color = "var(--status-warning)";
    td.append(w);
  }
  return td;
}

function grantForm(d: DsRow, clientId: number): HTMLElement {
  const form = el("form");
  form.style.display = "flex";
  form.style.flexWrap = "wrap";
  form.style.gap = "0.3rem";
  form.style.marginTop = "0.3rem";
  const evidence = el("input", {
    type: "text",
    placeholder: `Evidence* — who at ${d.owner} authorised this, when, reference`,
    required: true,
  }) as HTMLInputElement;
  evidence.style.minWidth = "22rem";
  const expiry = el("input", { type: "date", title: "Optional expiry (the registry's own expiry still applies)" }) as HTMLInputElement;
  if (d.expires) expiry.max = d.expires;
  const submit = el("button", { type: "submit" }, "Grant");
  const cancel = el("button", { type: "button" }, "Cancel");
  cancel.addEventListener("click", () => {
    openForm = null;
    redraw();
  });
  form.append(evidence, expiry, submit, cancel);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    submit.disabled = true;
    postJson<{ warnings: string[]; profile_after: string }>(`/api/data-access/clients/${clientId}/grants`, {
      dataset_key: d.key,
      evidence: evidence.value.trim(),
      expires_at: expiry.value || null,
    })
      .then((r) => {
        openForm = null;
        showToast(`Granted ${d.title}. New target profile ${r.profile_after}.${r.warnings.length ? " " + r.warnings.join(" ") : ""}`);
        void loadAll();
      })
      .catch((e: unknown) => {
        submit.disabled = false;
        showToast(e instanceof Error ? e.message : "Grant failed");
      });
  });
  return form;
}

function revokeForm(d: DsRow, clientId: number): HTMLElement {
  const form = el("form");
  form.style.display = "flex";
  form.style.gap = "0.3rem";
  form.style.marginTop = "0.3rem";
  const reason = el("input", { type: "text", placeholder: "Reason for revoking*", required: true }) as HTMLInputElement;
  reason.style.minWidth = "18rem";
  const submit = el("button", { type: "submit" }, "Revoke");
  const cancel = el("button", { type: "button" }, "Cancel");
  cancel.addEventListener("click", () => {
    openForm = null;
    redraw();
  });
  form.append(reason, submit, cancel);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    if (!d.grant) return;
    submit.disabled = true;
    postJson(`/api/data-access/clients/${clientId}/grants/${d.grant.id}/revoke`, { reason: reason.value.trim() })
      .then(() => {
        openForm = null;
        showToast(`Revoked ${d.title}.`);
        void loadAll();
      })
      .catch((e: unknown) => {
        submit.disabled = false;
        showToast(e instanceof Error ? e.message : "Revoke failed");
      });
  });
  return form;
}

function accessCell(d: DsRow, clientId: number): HTMLElement {
  const td = el("td", {});
  if (d.always_on) {
    td.append(el("span", { className: "caption" }, "Always on"));
    return td;
  }
  if (d.class === "prohibited") {
    td.append(el("span", { className: "caption" }, "Not available"));
    return td;
  }
  if (d.class === "client_private") {
    td.append(el("span", { className: "caption" }, "Own data only"));
    return td;
  }
  const box = el("input", { type: "checkbox" }) as HTMLInputElement;
  box.checked = d.grant !== null;
  const label = el("label", {});
  label.style.display = "inline-flex";
  label.style.gap = "0.3rem";
  label.append(box, el("span", {}, d.grant ? "Granted" : "Not granted"));
  if (!d.grant && d.grant_block_reason) {
    box.disabled = true;
    td.append(label, el("div", { className: "caption" }, d.grant_block_reason));
    return td;
  }
  box.addEventListener("change", () => {
    openForm = { key: d.key, kind: d.grant ? "revoke" : "grant" };
    redraw();
  });
  td.append(label);
  if (openForm && openForm.key === d.key) {
    td.append(openForm.kind === "grant" ? grantForm(d, clientId) : revokeForm(d, clientId));
    box.checked = openForm.kind === "grant";
  }
  return td;
}

function renderCouncils(root: HTMLElement, detail: ClientDetail): void {
  const row = el("div");
  row.style.display = "flex";
  row.style.flexWrap = "wrap";
  row.style.gap = "0.5rem";
  row.style.alignItems = "center";
  row.append(el("span", {}, "Councils in scope:"));
  const boxes: HTMLInputElement[] = [];
  for (const c of detail.all_councils) {
    const lab = el("label", {});
    const b = el("input", { type: "checkbox", value: c }) as HTMLInputElement;
    b.checked = detail.councils.includes(c);
    boxes.push(b);
    lab.append(b, document.createTextNode(` ${c}`));
    row.append(lab);
  }
  const save = el("button", {}, "Save councils");
  save.addEventListener("click", () => {
    save.disabled = true;
    postJson(`/api/data-access/clients/${detail.client.id}/councils`, {
      councils: boxes.filter((b) => b.checked).map((b) => b.value),
    })
      .then(() => {
        showToast("Council scope saved.");
        void loadAll();
      })
      .catch((e: unknown) => {
        save.disabled = false;
        showToast(e instanceof Error ? e.message : "Save failed");
      });
  });
  row.append(save);
  root.append(row);
}

function renderDatasets(root: HTMLElement, detail: ClientDetail): void {
  // Open decisions up front — some sit in collapsed groups (e.g. WWL
  // methodology left available to every organisation pending a team call).
  const pending = detail.datasets.filter((d) => d.decision_pending);
  if (pending.length) {
    const box = el("div", { className: "caption" });
    box.style.color = "var(--status-warning)";
    box.style.margin = "0.3rem 0 0.5rem";
    box.append(el("strong", {}, `Open decisions (${pending.length}):`));
    const ul = el("ul");
    ul.style.margin = "0.2rem 0 0 1.1rem";
    for (const d of pending) ul.append(el("li", {}, `${d.title} — ${d.decision_pending}`));
    box.append(ul);
    root.append(box);
  }
  const table = el("table", { className: "detail-table" });
  const head = document.createElement("tr");
  head.append(
    el("th", {}, "Dataset / data owner"),
    el("th", {}, "Access"),
    el("th", {}, "Evidence / licence / expiry"),
    el("th", {}, "Would hide (7 d)"),
  );
  table.append(head);
  const groups = new Map<string, DsRow[]>();
  for (const d of detail.datasets) {
    const list = groups.get(d.class) ?? [];
    list.push(d);
    groups.set(d.class, list);
  }
  for (const cls of CLASS_ORDER) {
    const rows = groups.get(cls);
    if (!rows || rows.length === 0) continue;
    const gh = document.createElement("tr");
    const th = el("th", {}, `${detail.class_labels[cls] ?? cls} (${rows.length})`);
    th.colSpan = 4;
    th.style.textAlign = "left";
    gh.append(th);
    table.append(gh);
    const collapsed = COLLAPSED_CLASSES.has(cls);
    const trs: HTMLTableRowElement[] = [];
    for (const d of rows) {
      const tr = document.createElement("tr");
      const hitsTd = el("td", { className: "caption" }, d.shadow_hits_7d ? String(d.shadow_hits_7d) : "");
      if (d.shadow_hits_7d) hitsTd.style.color = "var(--status-warning)";
      tr.append(datasetCell(d), accessCell(d, detail.client.id), evidenceCell(d), hitsTd);
      if (collapsed) tr.style.display = "none";
      trs.push(tr);
      table.append(tr);
    }
    if (collapsed) {
      const toggle = el("button", {}, `show ${rows.length}`);
      toggle.style.marginLeft = "0.5rem";
      toggle.addEventListener("click", () => {
        const hidden = trs[0].style.display === "none";
        for (const tr of trs) tr.style.display = hidden ? "" : "none";
        toggle.textContent = hidden ? "hide" : `show ${rows.length}`;
      });
      th.append(toggle);
    }
  }
  root.append(scrollWrap(table));
}

function renderHistory(root: HTMLElement, detail: ClientDetail): void {
  const det = el("details");
  det.append(el("summary", { className: "caption" }, `Grant history (${detail.history.length}, append-only)`));
  const t = el("table", { className: "detail-table" });
  const head = document.createElement("tr");
  head.append(el("th", {}, "Dataset"), el("th", {}, "Status"), el("th", {}, "Granted"), el("th", {}, "Evidence"), el("th", {}, "Revoked"));
  t.append(head);
  for (const g of detail.history) {
    const tr = document.createElement("tr");
    tr.append(
      el("td", {}, g.dataset_key),
      el("td", {}, g.status),
      el("td", { className: "caption" }, `${fmtDate(g.granted_at)} ${g.granted_by_username ?? "P1 seed"}`),
      el("td", { className: "caption" }, g.evidence),
      el("td", { className: "caption" }, g.revoked_at ? `${fmtDate(g.revoked_at)} ${g.revoked_by_username ?? ""}: ${g.revoke_reason ?? ""}` : ""),
    );
    t.append(tr);
  }
  det.append(scrollWrap(t));
  root.append(det);
}

function renderMatrix(root: HTMLElement, m: Matrix): void {
  const det = el("details");
  det.append(el("summary", {}, "Matrix: organisations x restricted / licensed datasets"));
  const t = el("table", { className: "detail-table" });
  const head = document.createElement("tr");
  head.append(el("th", {}, "Dataset"), el("th", {}, "Owner"));
  for (const c of m.clients) head.append(el("th", {}, c.name));
  t.append(head);
  for (const d of m.grantable_datasets) {
    const tr = document.createElement("tr");
    tr.append(el("td", {}, d.title), el("td", { className: "caption" }, d.owner));
    for (const c of m.clients) tr.append(el("td", {}, d.granted_to.includes(c.id) ? "granted" : "-"));
    t.append(tr);
  }
  const prof = document.createElement("tr");
  prof.append(el("td", {}, "Compute profile"), el("td", {}, ""));
  for (const c of m.clients) {
    prof.append(el("td", { className: "caption" }, c.profile_id === m.public_profile_id ? `${c.profile_id} (public)` : c.profile_id));
  }
  t.append(prof);
  const sc = document.createElement("tr");
  sc.append(el("td", {}, "Would-hide requests (7 d)"), el("td", {}, ""));
  for (const c of m.clients) sc.append(el("td", { className: "caption" }, String(c.shadow_hits_7d)));
  t.append(sc);
  det.append(scrollWrap(t));
  root.append(det);
}

function renderShadow(root: HTMLElement, s: ShadowLog): void {
  const det = el("details");
  det.append(
    el(
      "summary",
      {},
      `Shadow / denied log: what enforcement hides (last 7 days; this server process checked ${s.requests_checked} requests, ${s.requests_would_hide} with a dataset miss, ${s.requests_denied ?? 0} denied outright)`,
    ),
  );
  if (s.counts.length === 0) {
    det.append(el("p", { className: "caption" }, "Nothing would have been hidden."));
  } else {
    const t = el("table", { className: "detail-table" });
    const head = document.createElement("tr");
    head.append(el("th", {}, "Organisation"), el("th", {}, "Dataset"), el("th", {}, "Surface"), el("th", {}, "Requests"), el("th", {}, "Last"));
    t.append(head);
    for (const r of s.counts.slice(0, 100)) {
      const tr = document.createElement("tr");
      tr.append(
        el("td", {}, r.client_name ?? String(r.client_id)),
        el("td", {}, r.dataset_key),
        el("td", { className: "caption" }, r.surface),
        el("td", {}, String(r.hits)),
        el("td", { className: "caption" }, `${new Date(r.last_at).toLocaleString()} ${r.last_username ?? ""} ${r.last_path ?? ""}`),
      );
      t.append(tr);
    }
    det.append(scrollWrap(t));
  }
  const denied = s.denied ?? [];
  if (denied.length > 0) {
    det.append(
      el("h4", {}, `Denied without a dataset key (this server process: ${s.requests_denied ?? denied.length}, newest first)`),
    );
    const t = el("table", { className: "detail-table" });
    const head = document.createElement("tr");
    head.append(el("th", {}, "When"), el("th", {}, "Organisation"), el("th", {}, "User"), el("th", {}, "Path"), el("th", {}, "Reason"));
    t.append(head);
    for (const r of denied.slice(0, 50)) {
      const tr = document.createElement("tr");
      tr.append(
        el("td", { className: "caption" }, new Date(r.at).toLocaleString()),
        el("td", {}, r.client_name ?? ""),
        el("td", { className: "caption" }, r.username ?? ""),
        el("td", { className: "caption" }, r.path),
        el("td", { className: "caption" }, r.reason),
      );
      t.append(tr);
    }
    det.append(scrollWrap(t));
  }
  if (s.unclassified.length > 0) {
    det.append(
      el(
        "p",
        { className: "caption" },
        "API paths not yet classified in config/data_registry.yaml (api_surfaces) - counted, never recorded as hidden: " +
          s.unclassified.slice(0, 20).map((u) => `${u.path} (${u.requests})`).join(", "),
      ),
    );
  }
  root.append(det);
}

function draw(root: HTMLElement): void {
  root.innerHTML = "";
  const header = el("div");
  header.style.display = "flex";
  header.style.alignItems = "center";
  header.style.gap = "0.5rem";
  header.style.flexWrap = "wrap";
  header.append(el("h2", {}, "Data access"));
  const refresh = el("button", {}, "Refresh");
  refresh.addEventListener("click", () => void loadAll());
  header.append(refresh);
  root.append(header);

  const mode = matrixCache?.mode ?? "";
  const modeText =
    mode === "enforce"
      ? "ENFORCING: each organisation sees only what it is granted (client 1 is unrestricted); everything else answers 404. "
      : mode === "shadow"
        ? "SHADOW MODE (escape hatch): grants are recorded and checked, but nothing is hidden; the counts below show what enforcement would hide. "
        : mode === "off"
          ? "CHECKS OFF: grants are neither enforced nor logged. "
          : "";
  const banner = el(
    "p",
    { className: "caption" },
    modeText +
      "Grants are per dataset x organisation, need evidence of the data owner's authority, and are append-only " +
      "(revoking keeps the history).",
  );
  root.append(banner);
  if (errorText) root.append(el("p", { className: "doc-viewer-error" }, errorText));
  if (!matrixCache) {
    root.append(el("p", { className: "caption" }, "Loading data access…"));
    return;
  }

  const pick = document.createElement("select");
  for (const c of matrixCache.clients) {
    const o = document.createElement("option");
    o.value = String(c.id);
    o.textContent = `${c.name} (${c.slug})`;
    o.selected = c.id === selectedClient;
    pick.append(o);
  }
  pick.addEventListener("change", () => {
    selectedClient = Number(pick.value);
    detailCache = null;
    openForm = null;
    redraw();
    void loadAll();
  });
  const pickRow = el("div");
  pickRow.style.display = "flex";
  pickRow.style.flexWrap = "wrap";
  pickRow.style.gap = "0.5rem";
  pickRow.style.alignItems = "center";
  pickRow.append(el("span", {}, "Organisation:"), pick);
  root.append(pickRow);

  const d = detailCache;
  if (!d || d.client.id !== selectedClient) {
    root.append(el("p", { className: "caption" }, "Loading organisation…"));
  } else {
    renderCouncils(root, d);
    const isPublic = d.context.profile_id === d.context.public_profile_id;
    root.append(
      el(
        "p",
        { className: "caption" },
        `Target profile ${d.context.profile_id}${isPublic ? " (public only)" : ""}. Serving: ` +
          (d.state.serving === "bundle" ? "serving bundle" : "current pipeline outputs (data/interim) - per-profile serving bundles arrive in P2."),
      ),
    );
    renderDatasets(root, d);
    renderHistory(root, d);
  }
  renderMatrix(root, matrixCache);
  if (shadowCache) renderShadow(root, shadowCache);
}

/** Renders into the Data access tab (platform_admin only - admin.ts hides the
 * tab for anyone else; every route behind it is platform_admin-gated anyway). */
export function renderDataAccessSection(root: HTMLElement): void {
  const host = el("div");
  root.append(host);
  hostEl = host;
  draw(host);
  if (matrixCache === null) void loadAll();
}
